import { AppSession } from './app/interfaces/app-session';

/**
 * Pure helpers for the legacy → vive-vendors redirect decision.
 *
 * Kept out of main.ts so the branching logic is unit-testable without the
 * pre-bootstrap orchestration (PostHog, fetch, window.location, timers).
 *
 *   legacy app load
 *        │
 *        ├─ signed-out registration/login URL ──► buildVendorRedirectUrl()
 *        │        (CompanyId from the URL path, matched against the flag payload)
 *        │
 *        └─ signed-in vendor (vendorIdFromSession) ──► shouldRedirectAuthenticatedVendor()
 *                 (sticky useVendorPortal flag OR CompanyId ∈ flag payload),
 *                 then buildVendorPortalDestinationUrl(); vive-vendors reads the
 *                 same session cookie, so the URL carries nothing.
 */

export interface PortalRouting {
  useVendorPortal: boolean;
  companyIds: number[];
}

/** sessionStorage key marking that this tab already handed off to vive-vendors. */
export const HANDOFF_GUARD_KEY = 'vendor-app-handed-off';

/**
 * Match a set of CompanyIds against the `vendor-app-redirect` PostHog flag
 * payload. Absent/empty payload = full rollout (match all). Coerces to string
 * on both sides — the payload is `string[]`, DB CompanyIds are numbers.
 */
export function matchesRedirectAllowlist(
  candidateCompanyIds: ReadonlyArray<string | number>,
  payload: string[] | null
): boolean {
  if (!payload || payload.length === 0) {
    return true;
  }
  const allow = new Set(payload.map((id) => String(id)));
  return candidateCompanyIds.some((id) => allow.has(String(id)));
}

/**
 * Decide whether an authenticated vendor should be force-redirected.
 * - Sticky `useVendorPortal` always redirects (bypasses the flag).
 * - Otherwise the flag must be enabled AND the vendor must belong to at least
 *   one company that matches the payload. A vendor with zero companies is never
 *   redirected by the flag (no client context to land in).
 */
export function shouldRedirectAuthenticatedVendor(
  routing: PortalRouting,
  flagEnabled: boolean,
  payload: string[] | null
): boolean {
  if (routing.useVendorPortal) {
    return true;
  }
  if (!flagEnabled || routing.companyIds.length === 0) {
    return false;
  }
  return matchesRedirectAllowlist(routing.companyIds, payload);
}

/**
 * The vendor whose portal routing decides a redirect, or null when this session
 * must never be redirected: signed out, not a vendor, a vendor still registering
 * (no VendorId yet), or an impersonation session.
 */
export function vendorIdFromSession(session: AppSession | null): number | null {
  if (!session || session.session.impersonation) {
    return null;
  }
  const { UserAccountType, VendorId } = session.user;
  return UserAccountType?.name === 'Vendor' && typeof VendorId === 'number'
    ? VendorId
    : null;
}

/** Extract the CompanyId from a legacy registration URL path (/company/:id/). */
export function companyIdFromPath(path: string): string | null {
  return path.match(/\/company\/([^/]+)/)?.[1] ?? null;
}

/**
 * Build the vive-vendors target for an UNAUTHENTICATED registration/login URL,
 * preserving the path + query string. Returns null for non-redirectable paths.
 */
export function buildVendorRedirectUrl(
  vendorsBaseUrl: string,
  path: string,
  search: string
): string | null {
  // /vendor-signup/:vendorSignUpCode/...
  if (/^\/vendor-signup\//.test(path)) {
    return `${vendorsBaseUrl}${path}${search}`;
  }
  // The password-reset subtree (/login/forgot-password and
  // /login/forgot-password/reset/:code) is NOT a vendor-signup deep link and must
  // never be redirected to the vendor portal. It is served here in vive-app-v2;
  // vive-vendors has no such route, so redirecting it 404'd password reset for
  // every non-vendor user once the vendor-app-redirect flag rolled out. This guard
  // must precede the broad /login/:seg/ match below, which otherwise matches
  // "forgot-password" as the :seg (VPDW-3605).
  if (/^\/login\/forgot-password(\/|$)/.test(path)) {
    return null;
  }
  // /login/:vendorSignUpCode/complianceType/... (second segment distinguishes
  // it from the plain /login route)
  if (/^\/login\/[^/]+\//.test(path)) {
    return `${vendorsBaseUrl}${path}${search}`;
  }
  return null;
}

/**
 * VPDW-4225 — the vive-vendors path that replaces a legacy wizard URL.
 *
 * The legacy route's `:paymentType` segment is what distinguished the two flows
 * (`.../registration` vs `.../renewal`), so it picks the destination:
 *
 *   - `renewal`      → `/renewal`, the renewal wizard (first step is client
 *                      selection, so it needs no CompanyId).
 *   - anything else  → `/registration/:companyId`, documented in vive-vendors'
 *                      routes as the entry point "preserved ... for flows that
 *                      resume registration without a vendorSignUpCode".
 *
 * With no CompanyId (the `/bundle/:BundleId/...` variant) there is no client to
 * resume, so it falls back to the portal root and lets its own routing decide.
 */
export function legacyWizardPortalPath(
  paymentType: string | null,
  companyId: string | null
): string {
  if (paymentType === 'renewal') {
    return '/renewal';
  }
  if (companyId == null || companyId.length === 0) {
    return '/';
  }
  return `/registration/${companyId}`;
}

/**
 * The vive-vendors URL for a specific page. Nothing rides along: vive-vendors
 * reads the same session cookie, and an unauthenticated vendor (the emailed-link
 * case) goes through its own login and back.
 */
export function buildVendorPortalDestinationUrl(
  vendorsBaseUrl: string,
  path: string
): string {
  const base = vendorsBaseUrl.replace(/\/+$/, '');
  return `${base}${path}`;
}

/**
 * Defense-in-depth before sending the user to `vendorsBaseUrl`. `vendorsAppUrl`
 * is hardcoded per-environment in `config.ts` and isn't user-controllable, so
 * this only trips on a config regression (typo, stray override). Allow `https`
 * on any host; allow `http` only for localhost dev.
 */
export function isSafeVendorsBaseUrl(vendorsBaseUrl: string): boolean {
  try {
    const url = new URL(vendorsBaseUrl);
    if (url.protocol === 'https:') {
      return true;
    }
    return url.protocol === 'http:' && url.hostname === 'localhost';
  } catch {
    return false;
  }
}

export function hasAlreadyHandedOff(storage: Storage): boolean {
  return storage.getItem(HANDOFF_GUARD_KEY) === 'true';
}

export function markHandedOff(storage: Storage): void {
  storage.setItem(HANDOFF_GUARD_KEY, 'true');
}
